import { AdSlot } from "@/components/AdSlot";
import { createFileRoute, Link, notFound } from "@tanstack/react-router";
import ReactMarkdown from "react-markdown";
import remarkGfm from "remark-gfm";
import { getBlogPost } from "@/lib/blog.functions";
import { breadcrumbLd, faqLd, speakableLd, pageTitle } from "@/lib/seo";
import { deriveArticleFaqs, articleBluf } from "@/lib/blog-faq";
import { FaqBlock } from "@/components/FaqBlock";
import { MaintenanceSpecLink } from "@/components/MaintenanceSpecLink";
import { maintenanceSpecArticleLink } from "@/lib/maintenance-spec";

export const Route = createFileRoute("/blog/$slug")({
  loader: async ({ params }) => {
    const { post, related } = await getBlogPost({ data: { slug: params.slug } });
    if (!post) throw notFound();
    return { post, related };
  },
  head: ({ loaderData, params }) => {
    if (!loaderData?.post) {
      return {
        meta: [
          { title: "Article not found — AutoSpecData" },
          { name: "robots", content: "noindex" },
        ],
      };
    }
    const { post } = loaderData;
    const url = `https://autospecdata.com/blog/${params.slug}`;
    const description =
      post.meta_description ??
      post.excerpt ??
      `${post.title} — a plain-English guide from AutoSpecData.`;
    const metaTitle = post.meta_title ?? post.title;
    const faqs = deriveArticleFaqs(post.content_markdown);
    // An H1 identical to the <title> gets flagged as a duplicate; the article
    // H1 must stay the headline, so differentiate the title tag instead when
    // the brand suffix did not fit.
    let headTitle = pageTitle(metaTitle);
    if (headTitle === post.title) headTitle = pageTitle(`${metaTitle} Guide`);
    return {
      meta: [
        { title: headTitle },
        { name: "description", content: description },
        { property: "og:title", content: metaTitle },
        { property: "og:description", content: description },
        { property: "og:url", content: url },
        { property: "og:type", content: "article" },
        { name: "twitter:card", content: "summary_large_image" },
        { property: "og:image", content: categoryHeroImage(post.category) },
        { name: "twitter:image", content: categoryHeroImage(post.category) },
      ],
      links: [{ rel: "canonical", href: url }],
      scripts: [
        {
          type: "application/ld+json",
          children: JSON.stringify({
            "@context": "https://schema.org",
            "@type": "Article",
            headline: post.title,
            description,
            datePublished: post.published_at,
            dateModified: post.updated_at,
            mainEntityOfPage: url,
            articleSection: post.category,
            // Reference the root Organization node instead of repeating it —
            // duplicate entity nodes are the top Search Console validation error.
            author: { "@id": "https://autospecdata.com/#organization" },
            publisher: { "@id": "https://autospecdata.com/#organization" },
          }),
        },
        {
          type: "application/ld+json",
          children: JSON.stringify(speakableLd(url)),
        },
        // Only emitted when the article really contains question sections; the
        // same pairs are rendered visibly at the foot of the article.
        ...(faqs.length
          ? [{ type: "application/ld+json", children: JSON.stringify(faqLd(faqs)) }]
          : []),
        {
          type: "application/ld+json",
          children: JSON.stringify(
            breadcrumbLd([
              { name: "Home", path: "/" },
              { name: "Blog", path: "/blog" },
              { name: post.title, path: `/blog/${params.slug}` },
            ]),
          ),
        },
      ],
    };
  },
  component: BlogArticle,
});

function categoryHeroImage(category: string): string {
  const normalized = category.toLowerCase();
  if (normalized.includes("truck")) {
    return "/blog/truck-hero.jpg";
  }
  if (normalized.includes("family")) {
    return "/blog/family-suv-hero.jpg";
  }
  // Default: Buying / Buying Guides / SUVs / Fuel Economy / Maintenance / Towing
  return "/blog/buying-guide-hero.jpg";
}

function BlogArticle() {
  const { post, related } = Route.useLoaderData();
  const heroUrl = categoryHeroImage(post.category);
  const faqs = deriveArticleFaqs(post.content_markdown);
  const bluf = articleBluf(post.excerpt, post.content_markdown);
  const published = post.published_at
    ? new Date(post.published_at).toLocaleDateString("en-US", {
        year: "numeric",
        month: "long",
        day: "numeric",
      })
    : "";

  return (
    <div>
      <nav className="mx-auto max-w-3xl px-4 pt-4 text-sm text-muted-foreground sm:px-6">
        <Link to="/" className="hover:text-foreground">
          Home
        </Link>
        <span className="mx-2">/</span>
        <Link to="/blog" className="hover:text-foreground">
          Blog
        </Link>
        <span className="mx-2">/</span>
        <span className="text-foreground">{post.title}</span>
      </nav>

      <article className="mx-auto max-w-3xl px-4 py-10 sm:px-6">
        <p className="eyebrow text-primary">{post.category}</p>
        <h1 className="mt-2 text-3xl font-bold leading-tight text-foreground sm:text-4xl">
          {post.title}
        </h1>

        <figure className="mt-6 overflow-hidden rounded-2xl border border-border bg-card shadow-soft">
          <img
            src={heroUrl}
            alt={post.title}
            loading="eager"
            decoding="async"
            className="h-auto w-full max-w-full object-cover"
            width={1200}
            height={675}
            style={{ aspectRatio: "16 / 9" }}
          />
        </figure>

        {published ? (
          <p className="mt-5 text-sm text-muted-foreground">Published {published}</p>
        ) : null}
        {bluf ? (
          <aside
            data-aeo="atomic-passage"
            aria-label="Quick answer summary"
            className="mt-4 rounded-xl border border-border/80 bg-muted/40 p-4 shadow-sm"
          >
            <p className="text-lg leading-relaxed text-foreground">{bluf}</p>
          </aside>
        ) : null}

        <div className="prose-article mt-8">
          <ReactMarkdown
            remarkPlugins={[remarkGfm]}
            components={{
              // The page already renders the title as the single <h1>; the
              // markdown's own H1 becomes an H2 so headings stay hierarchical.
              h1: ({ children }) => <h2>{children}</h2>,
            }}
          >
            {post.content_markdown}
          </ReactMarkdown>
        </div>

        {maintenanceSpecArticleLink(post.slug) && (
          <div className="mt-8">
            <MaintenanceSpecLink
              heading="Manufacturer maintenance schedules"
              body="MaintenanceSpec publishes source-backed maintenance schedules by vehicle, covering mileage, time and condition-based service rules."
              anchor={`See ${maintenanceSpecArticleLink(post.slug)!.anchor} on MaintenanceSpec`}
              href={maintenanceSpecArticleLink(post.slug)!.url}
            />
          </div>
        )}

        <AdSlot placement="blog" />



        {faqs.length > 0 ? (
          <section className="mt-12 border-t border-border pt-8">
            <h2 className="font-heading text-xl font-semibold text-foreground">
              Frequently asked questions
            </h2>
            <div className="mt-4">
              <FaqBlock items={faqs} />
            </div>
          </section>
        ) : null}

        <div className="mt-12 rounded-2xl border border-border bg-card p-5">
          {related.length ? (
            <>
              <h2 className="font-heading text-base font-semibold text-foreground">
                Related guides
              </h2>
              <ul className="mt-3 space-y-2 text-sm">
                {related.map((r) => (
                  <li key={r.slug}>
                    <Link
                      to="/blog/$slug"
                      params={{ slug: r.slug }}
                      className="text-primary hover:underline"
                    >
                      {r.title}
                    </Link>
                    <span className="ml-2 text-xs text-muted-foreground">{r.category}</span>
                  </li>
                ))}
              </ul>
              <hr className="my-5 border-border" />
            </>
          ) : null}
          <h2 className="font-heading text-base font-semibold text-foreground">
            Keep going
          </h2>
          <div className="mt-3 flex flex-wrap gap-3 text-sm">
            <Link to="/best" className="cta-gold">
              Vehicle rankings
            </Link>
            <Link to="/tools" className="text-primary hover:underline">
              Calculators
            </Link>
            <Link to="/methodology" className="text-primary hover:underline">
              Our methodology
            </Link>
            <Link to="/blog" className="text-primary hover:underline">
              All guides
            </Link>
          </div>
        </div>
      </article>
    </div>
  );
}
